import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import CookieBanner from '@/components/CookieBanner'
import PelviaLogo from '@/components/PelviaLogo'
import Seo from '@/components/Seo'
import SiteHeader from '@/components/SiteHeader'
import TypeSwap from '@/components/TypeSwap'
import CareModel from '@/components/landing/CareModel'
import ConditionTeasers from '@/components/landing/ConditionTeasers'
import { useScrollReveal } from '@/hooks/useScrollReveal'
import { signInUrl, signUpUrl } from '@/lib/auth'
import './landing.css'

// Order must match the on-page vertical order of the sections — useDotNav picks
// the active dot by walking this list top-to-bottom, so a mismatch highlights
// the wrong dot. (AppWaitlist now renders right after the hero.)
const DOT_SECTIONS = [
  { id: 'hero', label: 'Home' },
  { id: 'app-waitlist', label: 'PelviaCare+' },
  { id: 'care-model', label: 'Care model' },
  { id: 'conditions', label: 'Conditions' },
  { id: 'mission', label: 'Mission' },
] as const

type DotId = (typeof DOT_SECTIONS)[number]['id']

function useDotNav() {
  const [activeId, setActiveId] = useState<DotId>('hero')
  const [dotsVisible, setDotsVisible] = useState(false)
  const [onDark, setOnDark] = useState(false)

  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const scrollY = window.scrollY
        const windowH = window.innerHeight
        const hero = document.querySelector<HTMLElement>('section.hero')
        if (hero) setDotsVisible(scrollY > hero.offsetHeight * 0.3)

        let active: DotId = 'hero'
        for (const { id } of DOT_SECTIONS) {
          const el = id === 'hero' ? hero : document.getElementById(id)
          if (el) {
            const rect = el.getBoundingClientRect()
            if (rect.top <= windowH * 0.4) active = id
          }
        }
        setActiveId(active)

        const darkSections = document.querySelectorAll('.landing .final-cta, .landing footer.landing-footer')
        const center = windowH / 2
        let dark = false
        darkSections.forEach((s) => {
          const r = s.getBoundingClientRect()
          if (r.top < center && r.bottom > center) dark = true
        })
        setOnDark(dark)

        ticking = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return { activeId, dotsVisible, onDark }
}

function scrollToSection(id: DotId) {
  const el = id === 'hero' ? document.querySelector<HTMLElement>('section.hero') : document.getElementById(id)
  if (!el) return
  const offset = id === 'hero' ? 0 : 80
  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top, behavior: 'smooth' })
}

function useLaunchListWidget() {
  useEffect(() => {
    if (document.querySelector('script[data-launchlist="1"]')) return
    const s = document.createElement('script')
    s.src = 'https://getlaunchlist.com/js/widget-diy.js'
    s.defer = true
    s.dataset.launchlist = '1'
    document.head.appendChild(s)
  }, [])
}

function ArrowIcon() {
  return (
    <svg className="btn-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}

function DotNav({ activeId, visible, onDark }: { activeId: DotId; visible: boolean; onDark: boolean }) {
  return (
    <div className={`dot-nav${visible ? ' visible' : ''}${onDark ? ' on-dark' : ''}`}>
      {DOT_SECTIONS.map(({ id, label }) => (
        <div key={id} className={`dot-nav-item${activeId === id ? ' active' : ''}`}>
          <span className="dot-nav-label">{label}</span>
          <button
            type="button"
            className="dot-nav-dot"
            aria-label={`Scroll to ${label}`}
            onClick={() => scrollToSection(id)}
          />
        </div>
      ))}
    </div>
  )
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-bg">
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />
      </div>
      <div className="hero-content">
        <PelviaLogo
          animation="breathing"
          size="clamp(88px, 13vw, 132px)"
          alt=""
          aria-hidden
          style={{ display: 'flex', margin: '0 auto 1.75rem' }}
        />
        <p className="hero-kicker reveal reveal-delay-1">
          The first digitized endometriosis &amp; adenomyosis clinic
        </p>
        <h1 className="hero-headline reveal reveal-delay-2">
          From symptom to <em>surgeon referral</em>{' '}
          <TypeSwap from="in seven years." to="in 72 hours." plainPrefix="in " />
        </h1>
        <p className="hero-sub reveal reveal-delay-3">
          The average woman waits <strong>seven years</strong> for a diagnosis. Pelvia collapses that into <strong>72 hours</strong>
          {' '}with a clinical platform built around endometriosis, adenomyosis, and the women who&rsquo;ve waited too long.
        </p>
        <div className="hero-actions reveal reveal-delay-3">
          <a href={signUpUrl()} className="btn-primary">
            Start your intake
            <ArrowIcon />
          </a>
          <a href="#care-model" className="btn-secondary">See how it works</a>
        </div>
        <a href={signInUrl()} className="hero-login-link reveal reveal-delay-4">
          Already have an account? <span>Log in</span>
        </a>
        <div className="hero-trust reveal reveal-delay-4">
          <span className="hero-trust-item">Built by OBGYNs</span>
          <span className="hero-trust-divider" aria-hidden="true">&middot;</span>
          <span className="hero-trust-item">Board-certified physician sign-off</span>
          <span className="hero-trust-divider" aria-hidden="true">&middot;</span>
          <span className="hero-trust-item">Encrypted &amp; de-identified</span>
        </div>
      </div>
    </section>
  )
}


function AppWaitlist() {
  useLaunchListWidget()
  return (
    <section className="app-waitlist" id="app-waitlist">
      <div className="section-container">
        <div className="app-waitlist-inner">
          <div className="app-waitlist-badge reveal">
            <span className="app-waitlist-badge-dot" />
            PelviaCare+ &middot; Limited early access
          </div>
          <h2 className="section-title reveal reveal-delay-1">
            Join the <em>PelviaCare+</em> waitlist.
          </h2>
          <p className="app-waitlist-desc reveal reveal-delay-2">
            PelviaCare+ is the full Pelvia care model: from your first symptom to a board-certified
            physician sign-off and an excision-surgeon referral, in 72 hours instead of seven years.
            Join the waitlist to be among the first to get the right care, faster.
          </p>
          <form
            className="launchlist-form app-waitlist-form reveal reveal-delay-3"
            action="https://getlaunchlist.com/s/a13QQx"
            method="POST"
          >
            <div className="app-waitlist-input-group">
              <input name="email" type="email" placeholder="Enter your email" required />
              <button type="submit">Join the Waitlist</button>
            </div>
          </form>
          <p className="app-waitlist-note reveal reveal-delay-4">Free to join. No spam. You&rsquo;ll be first to know when we open access.</p>
          <div className="app-waitlist-features reveal reveal-delay-5">
            <span className="app-waitlist-feature">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Symptom to referral in 72h
            </span>
            <span className="app-waitlist-feature">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Board-certified sign-off
            </span>
            <span className="app-waitlist-feature">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Excision-surgeon referrals
            </span>
            <span className="app-waitlist-feature">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Private &amp; encrypted
            </span>
            <section className="backed-by" aria-label="Backed by UConn Health, in partnership with EndoForAll">
              <div className="backed-by-inner">
                <span className="section-label backed-by-label">Backed by</span>
                <img
                  className="backed-by-logo"
                  src="/static/app/uconn-health-logo.svg"
                  alt="UConn Health"
                  width={236}
                  height={28}
                />
                <span className="section-label backed-by-label">In partnership with</span>
                <img
                  className="backed-by-logo backed-by-logo--eefa"
                  src="/static/app/endoforall-logo.svg"
                  alt="EndoForAll"
                  width={362}
                  height={58}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  )
}

function Testimonial() {
  return (
    <section className="testimonial" aria-label="What members say">
      <div className="section-container">
        <div className="testimonial-card reveal">
          <div className="section-label testimonial-label">From a member</div>
          <svg className="testimonial-quote-mark" width="44" height="36" viewBox="0 0 44 36" fill="none" aria-hidden="true">
            <path d="M0 36V22.5C0 16.4 1.4 11.2 4.2 7C7 2.8 11.2 0.3 16.6 -0.5L18.4 4.5C15.1 5.3 12.6 7 11 9.5C9.4 12 8.6 14.9 8.6 18.3H18V36H0ZM25.6 36V22.5C25.6 16.4 27 11.2 29.8 7C32.6 2.8 36.8 0.3 42.2 -0.5L44 4.5C40.7 5.3 38.2 7 36.6 9.5C35 12 34.2 14.9 34.2 18.3H43.6V36H25.6Z" fill="currentColor" opacity="0.18"/>
          </svg>
          <blockquote className="testimonial-quote">
            Pelvia is one of the best tracker apps I&rsquo;ve ever used. It&rsquo;s super easy to navigate but also gives so many opportunities to add and use so much detail without being overwhelming. It&rsquo;s so quick and easy to use which is super important on flare up days to not take too much energy. I love how it recognises patterns and makes suggestions which can back up my own connections and ideas and makes it a lot easier to <em>believe in my symptoms and ideas.</em> I&rsquo;ve recommended it to so many friends and family. It really is an amazing project with amazing people behind it.
          </blockquote>
          <a
            className="testimonial-attribution"
            href="https://www.instagram.com/allthings._m"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="@allthings._m on Instagram"
          >
            <img
              src="/static/app/allthings-m-avatar.jpg"
              alt=""
              className="testimonial-avatar"
              loading="lazy"
            />
            <span className="testimonial-attribution-meta">
              <span className="testimonial-handle">@allthings._m</span>
              <span className="testimonial-verify">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
                Instagram
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 17 17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              </span>
            </span>
          </a>
        </div>
        <Link to="/pelviacare" className="testimonial-app-link reveal reveal-delay-1">
          Explore PelviaCare+
          <ArrowIcon />
        </Link>
      </div>
    </section>
  )
}

function MissionTeaser() {
  return (
    <section className="mission-teaser" id="mission">
      <div className="section-container">
        <div className="mission-teaser-card reveal">
          <div className="section-label">Our mission</div>
          <h2 className="mission-teaser-title">
            Built by people who <em>refused to look away.</em>
          </h2>
          <Link to="/mission" className="mission-teaser-link">
            Read our mission &amp; our founder&rsquo;s letter
            <ArrowIcon />
          </Link>
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="final-cta">
      <div className="final-cta-inner">
        <div className="section-label reveal">The 7-year wait ends here</div>
        <h2 className="reveal reveal-delay-1">
          Get the right care, <em>faster.</em>
        </h2>
        <p className="reveal reveal-delay-2">
          Complete an intake built to understand your pain, get a board certified physician&rsquo;s sign-off, and walk into a specialist&rsquo;s office ready. From symptom to surgeon referral in 72 hours.
        </p>
        <a href={signUpUrl()} className="btn-primary reveal reveal-delay-3">
          Start your intake
          <ArrowIcon />
        </a>
        <p className="final-cta-privacy reveal reveal-delay-4">
          Your health data is encrypted and de-identified. <strong>We never sell your personal data.</strong>
        </p>
      </div>
    </section>
  )
}

function LandingFooter() {
  return (
    <footer className="landing-footer">
      <div className="footer-inner">
        <div className="footer-left">
          <div className="footer-logo">
            <PelviaLogo size="100%" animation="none" />
          </div>
          <span className="footer-text">Pelvia.me</span>
        </div>
        <div className="footer-link-groups">
          <div className="footer-link-group">
            <span className="footer-link-group-label">Learn</span>
            <Link to="/endometriosis">Endometriosis</Link>
            <Link to="/adenomyosis">Adenomyosis</Link>
            <Link to="/excision-surgery">Excision surgery</Link>
            <Link to="/treatment">Treatment &amp; management</Link>
          </div>
          <div className="footer-link-group">
            <span className="footer-link-group-label">Product</span>
            <Link to="/pelviacare">PelviaCare+</Link>
          </div>
          <div className="footer-link-group">
            <span className="footer-link-group-label">Pelvia</span>
            <Link to="/mission">Mission</Link>
            <Link to="/security">Security</Link>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="mailto:support@pelvia.me">Support</a>
            <a href={signInUrl()}>Sign in</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default function LandingPage() {
  const { activeId, dotsVisible, onDark } = useDotNav()
  useScrollReveal()

  // Smooth-scroll in-page anchor links (any <a href="#...">).
  const rootRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      const anchor = target?.closest('a[href^="#"]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href === '#') return
      const el = document.querySelector<HTMLElement>(href)
      if (!el) return
      e.preventDefault()
      const top = el.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top, behavior: 'smooth' })
    }
    root.addEventListener('click', handler)
    return () => root.removeEventListener('click', handler)
  }, [])

  // Honor an inbound hash (e.g. arriving from a learn page's "/#care-model"
  // link), which React Router does not scroll to on its own.
  useEffect(() => {
    const hash = window.location.hash
    if (!hash || hash === '#') return
    const el = document.querySelector<HTMLElement>(hash)
    if (!el) return
    requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top, behavior: 'smooth' })
    })
  }, [])

  return (
    <div className="landing" ref={rootRef}>
      <Seo
        title="Pelvia | The first digitized endometriosis clinic"
        description="Pelvia collapses the 7-year wait for an endometriosis or adenomyosis diagnosis into 72 hours: AI-assisted intake, physician sign-off, and excision-surgeon referral."
        path="/"
        image="/static/og/home.png"
      />
      <DotNav activeId={activeId} visible={dotsVisible} onDark={onDark} />
      <SiteHeader />
      <Hero />
      <AppWaitlist />
      <CareModel />
      <ConditionTeasers />
      <Testimonial />
      <MissionTeaser />
      <FinalCta />
      <LandingFooter />
      <CookieBanner />
    </div>
  )
}
